import { Component, computed, effect, inject, viewChild, ViewContainerRef } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs/operators';

import { UserPreferencesFacade } from '@app/core/stores/user-preferences';
import { selectUserContext } from '@app/modules/authentication/store/auth/auth.selectors';
import { HeaderConfig } from '@app/core/models';
import { AppHeaderTheme } from './app-header-theme.model';
import { TopoLogoSVGComponent } from '@app/shared/components/svgs/topo-logo-svg.component';
import { AppHeaderMenuComponent, AppHelpComponent } from './components';

// importing these because the cascading changes to make the correct things standalone is too large of a change for now
// needed for the profile menu and notifications button
import { CoreModule as OldCoreModule } from '@app/modules/core/core.module';
import { NotificationsModule } from '@app/modules/notifications/notifications.module';
import { UserProfileFacade } from '@app/modules/core/store/user-profile';
import { getRouteData } from '../../utils/get-route-data';

@Component({
  selector: 'topo-app-header',
  imports: [TopoLogoSVGComponent, TranslatePipe, RouterLink, AppHeaderMenuComponent, AppHelpComponent, OldCoreModule, NotificationsModule],
  templateUrl: './app-header.component.html',
  styleUrl: './app-header.component.scss'
})
export class AppHeaderComponent {
  private userProfile = inject(UserProfileFacade);
  protected isSideNavEnabled = this.userProfile.sideNavEnabled;
  protected userPrefs = inject(UserPreferencesFacade);
  private route = inject(ActivatedRoute);

  protected configuration$ = inject(Router).events.pipe(
    filter(event => event instanceof NavigationEnd),
    map(() => {
      const { routeConfig = {} } = getRouteData(this.route.snapshot) ?? {};
      return routeConfig?.headerConfig;
    })
  );

  protected configuration = toSignal<HeaderConfig>(this.configuration$);
  readonly show = computed(() => !!this.configuration());

  protected theme = computed(() => {
    const config = this.configuration();

    if (!config) {
      return AppHeaderTheme.Default;
    }

    const { theme } = config;
    return !!theme ? (typeof theme === 'function' ? theme() : theme) : AppHeaderTheme.Default;
  });

  protected label = computed(() => {
    const { labelKey } = this.configuration() ?? {};

    if (!labelKey) {
      return;
    }

    return typeof labelKey === 'function' ? labelKey() : labelKey;
  });

  protected orgClientLogo = computed(() => {
    const userOrg = this.userProfile.userOrg();

    if (!userOrg) {
      return;
    }

    const { clientLogoDataUrl = null } = userOrg.configurationSettings ?? {};
    return clientLogoDataUrl;
  });

  protected textColor = computed(() => {
    const theme = this.theme();

    switch (theme) {
      case AppHeaderTheme.Purple:
        return 'var(--white)';
      case AppHeaderTheme.Orange:
        return 'var(--neutral-color-000)';
      default:
        return 'var(--neutral-color-800)';
    }
  });

  protected userContext = inject(Store).selectSignal(selectUserContext);
  protected showMenus = computed(() => {
    const userContext = this.userContext();

    if (!userContext) {
      return false;
    }

    const { isReadOnlyToken, isExternalLink } = userContext;

    if (isReadOnlyToken || isExternalLink) {
      return false;
    }

    const { hideMenu = false } = this.configuration() ?? {};
    return !(typeof hideMenu === 'function' ? hideMenu() : hideMenu);
  });

  protected showNotifications = computed(() => {
    const { isReadOnlyToken } = this.userContext();

    if (isReadOnlyToken) {
      return false;
    }

    const { notificationsDisabled = false } = this.configuration() ?? {};
    return !(typeof notificationsDisabled === 'function' ? notificationsDisabled() : notificationsDisabled);
  });

  private contentContainer = viewChild('content', { read: ViewContainerRef });

  contentEffect$ = effect(() => {
    const config = this.configuration();
    const container = this.contentContainer();

    if (!container || !config) {
      return;
    }

    container.clear();

    if (!!config.contentComponent) {
      container.createComponent(config.contentComponent());
    }
  });
}
